import { isAxiosError } from "axios";

/**
 * Checks if the given error comes from a request rejected because the user is
 * not allowed to perform it, either by session (401) or by permission (403).
 * @param error - The error caught from an axios request or a react-query hook.
 * @returns True if the error is a 401 or 403 response, false otherwise.
 */
export const isAccessDeniedError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403);

/**
 * Checks if the given errors object contains a specific error for a specific field.
 * @param errors - The errors object returned from the API. e.g., { name: ["validation.unique", "validation.format"] }
 * @param field - The field to check for errors. e.g., "name"
 * @param errorKey - The specific error key to check for. e.g., "validation.unique"
 * @returns True if the error exists, false otherwise.
 */
export const isAPIError = (
  data: {
    errors?: { [key: string]: string[] };
    message?: string;
  },
  field: string,
  errorKey: string,
): boolean => {
  if (!data || !field || !errorKey) return false;
  const errors = data.errors;
  if (errors) {
    if (!Array.isArray(errors[field])) return false;
    if (errors[field]?.includes(errorKey)) return true;
  } else if (data.message) {
    // Fallback for non-standard error responses
    // for specific duplicated name error messages
    const messageLower = data.message.toLowerCase();
    if (
      messageLower.includes("name") &&
      messageLower.includes("already exists") &&
      errorKey === "validation.unique" &&
      field === "name"
    ) {
      return true;
    }
  }
  return false;
};
